import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Camera, MapPin, AlertTriangle, Edit3 } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';

export default function Capture() {
  const nav = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);
  const fileRef2 = useRef(null);

  // Schema + questions
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState('');

  // Wizard step state
  const [step, setStep] = useState('init'); // 'init' | 'photo' | 'questions' | 'summary' | 'success'
  const [questionIndex, setQuestionIndex] = useState(0);

  // Photo
  const [photo, setPhoto] = useState(null); // File
  const [photoUrl, setPhotoUrl] = useState(null); // object URL
  const [photo2, setPhoto2] = useState(null); // File optionnelle
  const [photo2Url, setPhoto2Url] = useState(null); // object URL

  // GPS
  const [coords, setCoords] = useState(null); // { lat, lng, acc }
  const [gpsState, setGpsState] = useState('idle'); // idle | locating | ok | error | unsupported
  const [gpsError, setGpsError] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');

  // Reverse geocoding
  const [city, setCity] = useState('');
  const [cityCode, setCityCode] = useState('');
  const [geoLocating, setGeoLocating] = useState(false);

  // Form answers
  const [answers, setAnswers] = useState({});

  // Validation feedback (per-question)
  const [showRequiredError, setShowRequiredError] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Load schema on mount
  useEffect(() => {
    const loadSchema = async () => {
      try {
        const data = await api.get('/api/survey/schema');
        setSchema(data);
        setSchemaError('');
      } catch (err) {
        setSchemaError(
          (err && err.message) || 'Impossible de charger le formulaire.'
        );
      } finally {
        setSchemaLoading(false);
      }
    };
    loadSchema();
  }, []);

  // Cleanup object URL
  useEffect(() => {
    return () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, [photoUrl]);
  useEffect(() => {
    return () => {
      if (photo2Url) URL.revokeObjectURL(photo2Url);
    };
  }, [photo2Url]);

  // Reverse geocoding when coords change
  useEffect(() => {
    if (!coords) return;
    const doReverseGeo = async () => {
      setGeoLocating(true);
      try {
        const r = await fetch(
          `https://api-adresse.data.gouv.fr/reverse/?lon=${coords.lng}&lat=${coords.lat}`
        );
        const j = await r.json();
        const feat = j.features?.[0];
        if (feat) {
          setCity(feat.properties.city || feat.properties.municipality || '');
          setCityCode(feat.properties.citycode || '');
        }
      } catch {
        // Silently fail; network error on field doesn't block submission
      } finally {
        setGeoLocating(false);
      }
    };
    doReverseGeo();
  }, [coords]);

  // When entering a new question, reset the required-error display
  useEffect(() => {
    setShowRequiredError(false);
  }, [questionIndex, step]);

  const requestGeolocation = () => {
    if (!('geolocation' in navigator)) {
      setGpsState('unsupported');
      setGpsError('La géolocalisation n\'est pas disponible sur cet appareil.');
      return;
    }
    setGpsState('locating');
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        });
        setGpsState('ok');
      },
      (err) => {
        setGpsState('error');
        if (err.code === 1) {
          setGpsError('Permission de localisation refusée. Autorisez l\'accès puis réessayez.');
        } else if (err.code === 3) {
          setGpsError('La position n\'a pas pu être obtenue (timeout). Réessayez.');
        } else {
          setGpsError('Position introuvable. Réessayez.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const onStart = () => {
    setStep('photo');
    requestGeolocation();
    // Open camera. Must be triggered by user gesture.
    setTimeout(() => {
      if (fileRef.current) fileRef.current.click();
    }, 0);
  };

  // Cas idéal : la photo est passée en state par Home.jsx (file input cliqué
  // directement sur Home, donc user gesture préservé). On saute l'écran d'accueil
  // et on passe direct aux questions, et on lance le GPS.
  // Fallback : pas de photo en state → tenter l'auto-open de la caméra
  // (peut être bloqué par iOS Safari, l'utilisateur cliquera alors le bouton).
  useEffect(() => {
    const incoming = location.state?.photo;
    if (incoming && !photo) {
      setPhoto(incoming);
      setPhotoUrl(URL.createObjectURL(incoming));
      setStep('questions');
      setQuestionIndex(0);
      requestGeolocation();
      // Vide le state pour éviter le retraitement au refresh
      window.history.replaceState({}, '');
      return;
    }
    if (step === 'init' && !photo) {
      onStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhoto(f);
    setPhotoUrl(URL.createObjectURL(f));
    // Move to questions step on first photo capture
    if (step === 'photo' || step === 'init') {
      setStep('questions');
      setQuestionIndex(0);
    }
  };

  const onFile2Change = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (photo2Url) URL.revokeObjectURL(photo2Url);
    setPhoto2(f);
    setPhoto2Url(URL.createObjectURL(f));
    // On reste sur l'étape courante (pas de redirection)
  };

  const removePhoto2 = () => {
    if (photo2Url) URL.revokeObjectURL(photo2Url);
    setPhoto2(null);
    setPhoto2Url(null);
    if (fileRef2.current) fileRef2.current.value = '';
  };

  const finalCoords = useMemo(() => {
    if (coords) return coords;
    if (manualMode) {
      const lat = parseFloat(manualLat);
      const lng = parseFloat(manualLng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng, acc: null };
    }
    return null;
  }, [coords, manualMode, manualLat, manualLng]);

  // Find priority_flag question
  const priorityQuestion = useMemo(() => {
    if (!schema) return null;
    return schema.questions?.find((q) => q.is_priority_flag);
  }, [schema]);

  // Find anomaly_flag question
  const anomalyQuestion = useMemo(() => {
    if (!schema) return null;
    return schema.questions?.find((q) => q.is_anomaly_flag);
  }, [schema]);

  const questions = schema?.questions || [];
  const totalQuestions = questions.length;

  // Helper: is a value considered "answered" for the given question type?
  const isAnswered = (q, val) => {
    if (q.type === 'select' || q.type === 'text' || q.type === 'textarea' || q.type === 'number') {
      return val !== undefined && val !== null && (typeof val === 'string' ? val.trim().length > 0 : true);
    }
    if (q.type === 'multiselect') {
      return Array.isArray(val) && val.length > 0;
    }
    if (q.type === 'flag') {
      // Questions « Priorité Life » et « Anomalie » : pas besoin de cliquer NON.
      // Si pas cochée → considérée comme « NON » d'office, donc question répondue.
      if (q.is_priority_flag || q.is_anomaly_flag) return true;
      return val === true || val === false;
    }
    return false;
  };

  // Validation: list of missing required question labels
  const getMissingRequired = () => {
    const missing = [];
    questions.forEach((q, idx) => {
      if (!q.required) return;
      const val = answers[q.key];
      if (!isAnswered(q, val)) {
        missing.push({ index: idx, label: q.label || q.key });
      }
    });
    return missing;
  };

  const missingRequired = getMissingRequired();

  const handleAnswerChange = (questionKey, value) => {
    setAnswers((prev) => ({ ...prev, [questionKey]: value }));
  };

  const goNext = () => {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex((i) => i + 1);
    } else {
      setStep('summary');
    }
  };

  const goPrev = () => {
    if (questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
    } else {
      // Back to photo step (but keep photo)
      setStep('photo');
    }
  };

  const tryGoNext = () => {
    const q = questions[questionIndex];
    if (!q) return;
    const val = answers[q.key];
    if (q.required && !isAnswered(q, val)) {
      setShowRequiredError(true);
      return;
    }
    goNext();
  };

  const onSubmit = async () => {
    setSubmitError('');

    if (!photo) {
      setSubmitError('La photo est requise.');
      return;
    }
    if (!finalCoords) {
      setSubmitError('La position GPS n\'est pas encore disponible.');
      return;
    }
    if (missingRequired.length > 0) {
      setSubmitError(`Veuillez remplir: ${missingRequired.map((m) => m.label).join(', ')}`);
      return;
    }

    const fd = new FormData();
    fd.append('photo', photo);
    if (photo2) fd.append('photo2', photo2);
    fd.append('latitude', String(finalCoords.lat));
    fd.append('longitude', String(finalCoords.lng));
    if (city) fd.append('city', city);
    if (cityCode) fd.append('city_code', cityCode);
    fd.append('answers', JSON.stringify(answers));
    if (priorityQuestion) {
      fd.append('priority_life', answers[priorityQuestion.key] ? '1' : '');
    }
    if (anomalyQuestion) {
      fd.append('anomaly', answers[anomalyQuestion.key] ? '1' : '');
    }

    setSubmitting(true);
    try {
      await api.postForm('/api/avaloirs', fd);
      setStep('success');
      setTimeout(() => nav('/', { replace: true }), 800);
    } catch (err) {
      setSubmitError(
        (err && err.body && err.body.error) ||
        (err && err.message) ||
        'Échec de l\'envoi. Réessayez.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ----- GPS status panel (used in photo step) -----
  const renderGpsStatus = () => {
    if (gpsState === 'ok' && coords) {
      const acc = coords.acc != null ? ` (±${Math.round(coords.acc)}m)` : '';
      return (
        <div className="cap__gps cap__gps--ok">
          <div>Position acquise{acc}</div>
          {city && <div className="cap__gps-city">Localité: {city}</div>}
          {geoLocating && <div className="cap__gps-geo">Localisation en cours…</div>}
        </div>
      );
    }
    if (gpsState === 'locating') {
      return <div className="cap__gps cap__gps--info">Position en cours…</div>;
    }
    if (gpsState === 'error' || gpsState === 'unsupported') {
      return (
        <div className="cap__gps cap__gps--err">
          <div>{gpsError || 'Position indisponible.'}</div>
          <div className="cap__gps-actions">
            {gpsState !== 'unsupported' && (
              <button type="button" className="vrt-btn vrt-btn--ghost cap__gps-btn" onClick={requestGeolocation}>
                Réessayer GPS
              </button>
            )}
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost cap__gps-btn"
              onClick={() => setManualMode((m) => !m)}
            >
              {manualMode ? 'Masquer saisie manuelle' : 'Saisie manuelle'}
            </button>
          </div>
          {manualMode && (
            <div className="cap__manual">
              <input
                className="vrt-input"
                type="number"
                step="any"
                inputMode="decimal"
                placeholder="Latitude"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
              />
              <input
                className="vrt-input"
                type="number"
                step="any"
                inputMode="decimal"
                placeholder="Longitude"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
              />
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // ----- Per-question rendering (one per screen) -----
  const renderTile = (label, selected, onClick, opts = {}) => {
    const { variant = 'teal' } = opts; // 'teal' | 'priority'
    const cls = [
      'wz__tile',
      selected ? 'wz__tile--on' : '',
      variant === 'priority' ? 'wz__tile--priority' : '',
      selected && variant === 'priority' ? 'wz__tile--priority-on' : '',
    ].filter(Boolean).join(' ');
    return (
      <button type="button" className={cls} onClick={onClick} disabled={submitting}>
        <span className="wz__tile-label">{label}</span>
        {selected && (
          <span className="wz__tile-check" aria-hidden="true">
            <Check size={20} strokeWidth={3} />
          </span>
        )}
      </button>
    );
  };

  const renderCurrentQuestion = () => {
    const q = questions[questionIndex];
    if (!q) return null;
    const value = answers[q.key];

    if (q.type === 'select') {
      return (
        <div className="wz__question">
          <div className="wz__hint">Choisis une réponse</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <div className="wz__tiles">
            {q.options?.map((opt) => {
              const selected = value === opt.value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`wz__tile ${selected ? 'wz__tile--on' : ''}`}
                  onClick={() => {
                    handleAnswerChange(q.key, opt.value);
                    setShowRequiredError(false);
                    // Auto-advance (vers la question suivante OU le récap si dernière)
                    setTimeout(() => goNext(), 280);
                  }}
                  disabled={submitting}
                >
                  <span className="wz__tile-label">{opt.label}</span>
                  {selected && (
                    <span className="wz__tile-check" aria-hidden="true">
                      <Check size={20} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (q.type === 'multiselect') {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <div className="wz__question">
          <div className="wz__hint">Choisis une ou plusieurs réponses</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <div className="wz__tiles">
            {q.options?.map((opt) => {
              const selected = selectedValues.includes(opt.value);
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`wz__tile ${selected ? 'wz__tile--on' : ''}`}
                  onClick={() => {
                    setShowRequiredError(false);
                    if (selected) {
                      handleAnswerChange(q.key, selectedValues.filter((v) => v !== opt.value));
                    } else {
                      handleAnswerChange(q.key, [...selectedValues, opt.value]);
                    }
                  }}
                  disabled={submitting}
                >
                  <span className="wz__tile-label">{opt.label}</span>
                  {selected && (
                    <span className="wz__tile-check" aria-hidden="true">
                      <Check size={20} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="wz__counter">
            {selectedValues.length} réponse{selectedValues.length > 1 ? 's' : ''} sélectionnée{selectedValues.length > 1 ? 's' : ''}
          </div>
        </div>
      );
    }

    if (q.type === 'text') {
      return (
        <div className="wz__question">
          <div className="wz__hint">Saisis ta réponse</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <input
            id={`q-${q.key}`}
            className="vrt-input wz__input"
            type="text"
            value={value || ''}
            onChange={(e) => {
              handleAnswerChange(q.key, e.target.value);
              setShowRequiredError(false);
            }}
            placeholder="Tape ta réponse…"
            disabled={submitting}
            autoFocus
          />
        </div>
      );
    }

    if (q.type === 'textarea') {
      return (
        <div className="wz__question">
          <div className="wz__hint">Saisis ta réponse (optionnel)</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <textarea
            id={`q-${q.key}`}
            className="vrt-textarea wz__textarea"
            value={value || ''}
            onChange={(e) => {
              handleAnswerChange(q.key, e.target.value);
              setShowRequiredError(false);
            }}
            placeholder="Tape ta réponse…"
            rows={6}
            disabled={submitting}
          />
        </div>
      );
    }

    if (q.type === 'number') {
      return (
        <div className="wz__question">
          <div className="wz__hint">Saisis un nombre</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <input
            id={`q-${q.key}`}
            className="vrt-input wz__number"
            type="number"
            inputMode="numeric"
            value={value === undefined || value === null ? '' : value}
            onChange={(e) => {
              handleAnswerChange(q.key, e.target.value);
              setShowRequiredError(false);
            }}
            placeholder="0"
            disabled={submitting}
            autoFocus
          />
        </div>
      );
    }

    if (q.type === 'flag') {
      const isPriority = !!q.is_priority_flag;
      const isAnomaly = !!q.is_anomaly_flag;
      const isYes = value === true;
      const isNo = value === false;

      // --- Cas spécial : question Priorité Life / Anomalie (single-tile) ---
      // Au lieu d'imposer OUI/NON, on affiche une seule grosse tuile à cocher.
      // Pas cochée → NON d'office, on passe à la suite.
      if (isPriority || isAnomaly) {
        const cfg = isPriority
          ? {
              bannerLabel: 'PRIORITÉ ABSOLUE LIFE',
              hint: 'Coche uniquement si cet avaloir est en Priorité Life. Sinon, passe à la suite.',
              ctaOff: 'Cocher si Priorité Life',
              ctaOn: 'PRIORITÉ LIFE',
              alert: 'Cet avaloir sera marqué en rouge sur la carte.',
              tileMod: 'wz__big-tile--priority',
              tileOnMod: 'wz__big-tile--priority-on',
              questionMod: 'wz__question--priority',
              bannerMod: 'wz__priority-banner',
              alertMod: 'wz__priority-alert',
            }
          : {
              bannerLabel: 'ANOMALIE',
              hint: 'Coche uniquement si cet avaloir présente une anomalie. Sinon, passe à la suite.',
              ctaOff: 'Cocher si anomalie',
              ctaOn: 'ANOMALIE SIGNALÉE',
              alert: 'Cet avaloir sera marqué en violet sur la carte.',
              tileMod: 'wz__big-tile--anomaly',
              tileOnMod: 'wz__big-tile--anomaly-on',
              questionMod: 'wz__question--anomaly',
              bannerMod: 'wz__anomaly-banner',
              alertMod: 'wz__anomaly-alert',
            };

        return (
          <div className={`wz__question ${cfg.questionMod}`}>
            <div className={cfg.bannerMod}>
              <AlertTriangle size={16} />
              <span>{cfg.bannerLabel}</span>
            </div>
            <div className="wz__hint">{cfg.hint}</div>
            <h2 className="wz__label">{q.label}</h2>
            <button
              type="button"
              className={[
                'wz__big-tile',
                cfg.tileMod,
                'wz__big-tile--single',
                isYes ? `wz__big-tile--on ${cfg.tileOnMod}` : '',
              ].filter(Boolean).join(' ')}
              onClick={() => {
                handleAnswerChange(q.key, isYes ? false : true);
                setShowRequiredError(false);
              }}
              disabled={submitting}
              aria-pressed={isYes}
            >
              <span className="wz__big-text">{isYes ? cfg.ctaOn : cfg.ctaOff}</span>
              {isYes && <Check size={28} strokeWidth={3} className="wz__big-check" />}
            </button>
            {isYes && <div className={cfg.alertMod}>{cfg.alert}</div>}
          </div>
        );
      }

      // --- Cas standard flag (autre question type oui/non) ---
      return (
        <div className="wz__question">
          <div className="wz__hint">Choisis OUI ou NON</div>
          <h2 className="wz__label">
            {q.label}{q.required ? ' *' : ''}
          </h2>
          <div className="wz__yesno">
            <button
              type="button"
              className={`wz__big-tile ${isYes ? 'wz__big-tile--on' : ''}`}
              onClick={() => {
                handleAnswerChange(q.key, true);
                setShowRequiredError(false);
                setTimeout(() => goNext(), 280);
              }}
              disabled={submitting}
            >
              <span className="wz__big-text">OUI</span>
              {isYes && <Check size={28} strokeWidth={3} className="wz__big-check" />}
            </button>
            <button
              type="button"
              className={`wz__big-tile ${isNo ? 'wz__big-tile--on wz__big-tile--no-on' : ''}`}
              onClick={() => {
                handleAnswerChange(q.key, false);
                setShowRequiredError(false);
                setTimeout(() => goNext(), 280);
              }}
              disabled={submitting}
            >
              <span className="wz__big-text">NON</span>
              {isNo && <Check size={28} strokeWidth={3} className="wz__big-check" />}
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  // ----- Resolve a value to a human-readable label for the summary -----
  const resolveAnswer = (q) => {
    const val = answers[q.key];
    if (val === undefined || val === null || val === '') return null;
    if (q.type === 'select') {
      const opt = q.options?.find((o) => o.value === val);
      return opt ? opt.label : String(val);
    }
    if (q.type === 'multiselect') {
      if (!Array.isArray(val) || val.length === 0) return null;
      return val
        .map((v) => {
          const opt = q.options?.find((o) => o.value === v);
          return opt ? opt.label : String(v);
        })
        .join(', ');
    }
    if (q.type === 'flag') {
      if (val === true) return 'Oui';
      if (val === false) return 'Non';
      return null;
    }
    return String(val);
  };

  // ============================================================
  // EARLY RENDERS: loading / error
  // ============================================================
  if (schemaLoading) {
    return (
      <div className="cap">
        <Header />
        <main className="cap__main cap__main--center">
          <div className="cap__ready vrt-card">
            <p className="cap__lead">Chargement du formulaire…</p>
          </div>
        </main>
        <Footer />
        <style>{baseStyles}</style>
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="cap">
        <Header />
        <main className="cap__main cap__main--center">
          <div className="cap__ready vrt-card">
            <p className="cap__lead">{schemaError}</p>
            <button type="button" className="vrt-btn vrt-btn--ghost" onClick={() => nav('/')}>
              Retour
            </button>
          </div>
        </main>
        <Footer />
        <style>{baseStyles}</style>
      </div>
    );
  }

  // ============================================================
  // STEP: success
  // ============================================================
  if (step === 'success') {
    return (
      <div className="cap">
        <Header />
        <main className="cap__main cap__main--center">
          <div className="cap__success-card vrt-card">
            <div className="cap__success-check">
              <Check size={48} strokeWidth={3} />
            </div>
            <h2>Avaloir enregistré</h2>
            <p>Redirection en cours…</p>
          </div>
        </main>
        <Footer />
        <style>{baseStyles}</style>
      </div>
    );
  }

  // ============================================================
  // Hidden file input (always mounted)
  // ============================================================
  const hiddenFile = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFileChange}
        style={{ display: 'none' }}
      />
      <input
        ref={fileRef2}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile2Change}
        style={{ display: 'none' }}
      />
    </>
  );

  // ============================================================
  // STEP: init (welcome screen)
  // ============================================================
  if (step === 'init') {
    return (
      <div className="cap">
        <Header />
        <main className="cap__main cap__main--center">
          {hiddenFile}
          <div className="cap__ready vrt-card">
            <h1 className="cap__title">Nouvel avaloir</h1>
            <p className="cap__lead">
              Vous allez prendre une photo de l'avaloir. Votre position GPS sera
              automatiquement enregistrée.
            </p>
            <button type="button" className="vrt-btn vrt-btn--teal cap__big" onClick={onStart}>
              <Camera size={20} />
              Prendre la photo
            </button>
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              onClick={() => nav('/')}
            >
              Annuler
            </button>
          </div>
        </main>
        <Footer />
        <style>{baseStyles}</style>
      </div>
    );
  }

  // ============================================================
  // STEP: photo (waiting for photo / can re-take)
  // ============================================================
  if (step === 'photo') {
    return (
      <div className="cap">
        <Header />
        <main className="cap__main cap__main--center">
          {hiddenFile}
          <div className="cap__ready vrt-card">
            {photoUrl ? (
              <>
                <p className="cap__lead">Tu peux reprendre la photo si besoin, ou continuer.</p>
                <img src={photoUrl} alt="Aperçu de l'avaloir" className="cap__photo cap__photo--small" />
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost cap__big"
                  onClick={() => fileRef.current && fileRef.current.click()}
                >
                  <Camera size={20} />
                  Reprendre la photo
                </button>

                {/* 2ème photo optionnelle */}
                {photo2Url ? (
                  <div className="cap__photo2-wrap">
                    <div className="cap__photo2-label">Photo 2 (optionnelle)</div>
                    <img src={photo2Url} alt="Aperçu photo 2" className="cap__photo cap__photo--small" />
                    <div className="cap__photo2-actions">
                      <button
                        type="button"
                        className="vrt-btn vrt-btn--ghost"
                        onClick={() => fileRef2.current && fileRef2.current.click()}
                      >
                        <Camera size={16} />
                        Reprendre photo 2
                      </button>
                      <button
                        type="button"
                        className="vrt-btn vrt-btn--ghost"
                        onClick={removePhoto2}
                        style={{ color: 'var(--vrt-danger)' }}
                      >
                        Supprimer photo 2
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="vrt-btn vrt-btn--ghost cap__big"
                    onClick={() => fileRef2.current && fileRef2.current.click()}
                  >
                    <Camera size={20} />
                    Ajouter une 2ème photo (optionnelle)
                  </button>
                )}

                <button
                  type="button"
                  className="vrt-btn vrt-btn--teal cap__big"
                  onClick={() => { setStep('questions'); }}
                >
                  Continuer
                  <ChevronRight size={20} />
                </button>
              </>
            ) : (
              <>
                <p className="cap__lead">En attente de la photo…</p>
                <button
                  type="button"
                  className="vrt-btn vrt-btn--teal cap__big"
                  onClick={() => fileRef.current && fileRef.current.click()}
                >
                  <Camera size={20} />
                  Ouvrir l'appareil photo
                </button>
              </>
            )}
            {renderGpsStatus()}
            <button
              type="button"
              className="vrt-btn vrt-btn--ghost"
              onClick={() => nav('/')}
            >
              Annuler
            </button>
          </div>
        </main>
        <Footer />
        <style>{baseStyles}</style>
      </div>
    );
  }

  // ============================================================
  // STEP: questions (the wizard)
  // ============================================================
  if (step === 'questions') {
    if (totalQuestions === 0) {
      return (
        <div className="cap">
          <Header />
          <main className="cap__main cap__main--center">
            <div className="cap__ready vrt-card">
              <p className="cap__lead">Aucune question configurée. Voir l'administration.</p>
              <button type="button" className="vrt-btn vrt-btn--teal cap__big" onClick={() => setStep('summary')}>
                Aller au récap
                <ChevronRight size={20} />
              </button>
            </div>
          </main>
          <Footer />
          <style>{baseStyles}</style>
        </div>
      );
    }

    const q = questions[questionIndex];
    const val = answers[q?.key];
    const answered = q ? isAnswered(q, val) : false;
    const isLast = questionIndex === totalQuestions - 1;
    const progressPct = Math.round(((questionIndex + 1) / totalQuestions) * 100);

    // Next button label / behaviour
    const nextDisabled = q?.required && !answered;
    let nextLabel;
    if (isLast) {
      nextLabel = 'Voir le récap';
    } else if (!q?.required && !answered) {
      nextLabel = 'Passer cette question';
    } else {
      nextLabel = 'Question suivante';
    }

    return (
      <div className="cap">
        {hiddenFile}
        <Header />

        {/* Sticky progress bar */}
        <div className="wz__progress-wrap">
          <div className="wz__progress-row">
            <div className="wz__progress-text">
              Question {questionIndex + 1} / {totalQuestions}
            </div>
            <div className="wz__progress-meta">
              {photoUrl && (
                <img src={photoUrl} alt="" className="wz__mini-photo" />
              )}
              {city && (
                <span className="wz__city">
                  <MapPin size={14} />
                  {city}
                </span>
              )}
            </div>
          </div>
          <div className="wz__progress-bar">
            <div className="wz__progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <main className="cap__main wz__main">
          <div className="wz__container">
            {renderCurrentQuestion()}

            {showRequiredError && q?.required && !answered && (
              <div className="wz__required-error" role="alert">
                Cette question est obligatoire.
              </div>
            )}
          </div>
        </main>

        {/* Sticky footer with nav buttons */}
        <div className="wz__nav">
          <button
            type="button"
            className="vrt-btn vrt-btn--ghost wz__nav-btn wz__nav-prev"
            onClick={goPrev}
            disabled={submitting}
          >
            <ChevronLeft size={20} />
            Précédent
          </button>
          <button
            type="button"
            className={`vrt-btn vrt-btn--teal wz__nav-btn wz__nav-next ${nextDisabled ? 'is-disabled' : ''}`}
            onClick={tryGoNext}
            disabled={submitting || nextDisabled}
          >
            {nextLabel}
            <ChevronRight size={20} />
          </button>
        </div>

        <style>{baseStyles}</style>
      </div>
    );
  }

  // ============================================================
  // STEP: summary
  // ============================================================
  if (step === 'summary') {
    const canSubmit = !!photo && !!finalCoords && missingRequired.length === 0 && !submitting;

    return (
      <div className="cap">
        {hiddenFile}
        <Header />

        <main className="cap__main wz__summary-main">
          <div className="wz__container">
            <h1 className="cap__title cap__title--lg">Récapitulatif</h1>

            {missingRequired.length > 0 && (
              <div className="wz__summary-error" role="alert">
                <strong>Tu dois encore répondre à :</strong>
                <ul>
                  {missingRequired.map((m) => (
                    <li key={m.index}>
                      <button
                        type="button"
                        className="wz__summary-link"
                        onClick={() => { setStep('questions'); setQuestionIndex(m.index); }}
                      >
                        {m.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!photo && (
              <div className="wz__summary-error" role="alert">
                La photo est manquante.
              </div>
            )}
            {!finalCoords && (
              <div className="wz__summary-error" role="alert">
                La position GPS n'est pas disponible.
              </div>
            )}

            {photoUrl && (
              <div className="wz__sum-photo-wrap">
                <img src={photoUrl} alt="Photo de l'avaloir" className="wz__sum-photo" />
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost wz__sum-retake"
                  onClick={() => fileRef.current && fileRef.current.click()}
                  disabled={submitting}
                >
                  <Camera size={16} />
                  Reprendre
                </button>
              </div>
            )}

            {/* Photo 2 (optionnelle) */}
            {photo2Url ? (
              <div className="wz__sum-photo-wrap" style={{ marginTop: 8 }}>
                <img src={photo2Url} alt="Photo 2 de l'avaloir" className="wz__sum-photo" />
                <button
                  type="button"
                  className="vrt-btn vrt-btn--ghost wz__sum-retake"
                  onClick={() => fileRef2.current && fileRef2.current.click()}
                  disabled={submitting}
                >
                  <Camera size={16} />
                  Reprendre photo 2
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="vrt-btn vrt-btn--ghost"
                style={{ marginTop: 8 }}
                onClick={() => fileRef2.current && fileRef2.current.click()}
                disabled={submitting}
              >
                <Camera size={16} />
                Ajouter une 2ème photo
              </button>
            )}

            <div className="wz__sum-section">
              <div className="wz__sum-section-title">
                <MapPin size={16} /> Position
              </div>
              {finalCoords ? (
                <div className="wz__sum-row">
                  <div className="wz__sum-q">Coordonnées</div>
                  <div className="wz__sum-a">
                    {finalCoords.lat.toFixed(6)}, {finalCoords.lng.toFixed(6)}
                    {finalCoords.acc != null && ` (±${Math.round(finalCoords.acc)}m)`}
                  </div>
                </div>
              ) : (
                <div className="wz__sum-row">
                  <div className="wz__sum-q">Coordonnées</div>
                  <div className="wz__sum-a wz__sum-a--missing">Pas de position</div>
                </div>
              )}
              {city && (
                <div className="wz__sum-row">
                  <div className="wz__sum-q">Ville</div>
                  <div className="wz__sum-a">{city}</div>
                </div>
              )}
            </div>

            <div className="wz__sum-section">
              <div className="wz__sum-section-title">Réponses</div>
              {questions.map((q, idx) => {
                const resolved = resolveAnswer(q);
                const isMissingRequired = q.required && !resolved;
                return (
                  <button
                    key={q.key}
                    type="button"
                    className={`wz__sum-row wz__sum-row--clickable ${isMissingRequired ? 'wz__sum-row--missing' : ''}`}
                    onClick={() => { setStep('questions'); setQuestionIndex(idx); }}
                    disabled={submitting}
                  >
                    <div className="wz__sum-q">{q.label}{q.required ? ' *' : ''}</div>
                    <div className={`wz__sum-a ${!resolved ? 'wz__sum-a--missing' : ''}`}>
                      {resolved || 'Pas de réponse'}
                    </div>
                    <Edit3 size={14} className="wz__sum-edit" />
                  </button>
                );
              })}
            </div>

            {submitError && (
              <div className="wz__required-error" role="alert">{submitError}</div>
            )}
          </div>
        </main>

        <div className="wz__nav">
          <button
            type="button"
            className="vrt-btn vrt-btn--ghost wz__nav-btn wz__nav-prev"
            onClick={() => { setStep('questions'); setQuestionIndex(Math.max(0, totalQuestions - 1)); }}
            disabled={submitting}
          >
            <ChevronLeft size={20} />
            Modifier
          </button>
          <button
            type="button"
            className="vrt-btn vrt-btn--teal wz__nav-btn wz__nav-next"
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {submitting ? 'Envoi…' : 'Valider et enregistrer'}
            {!submitting && <Check size={20} />}
          </button>
        </div>

        <style>{baseStyles}</style>
      </div>
    );
  }

  // Fallback (should not happen)
  return null;
}

// ============================================================
// CSS — extracted as a constant so all early-returns can reuse it
// ============================================================
const baseStyles = `
  .cap { min-height: 100vh; display: flex; flex-direction: column; background: var(--vrt-bg-alt); }
  .cap__main { flex: 1; padding: 16px; display: flex; flex-direction: column; align-items: stretch; max-width: 640px; width: 100%; margin: 0 auto; }
  .cap__main--center { justify-content: flex-start; }
  .cap__title { margin: 0 0 8px; font-size: 22px; color: var(--vrt-navy); }
  .cap__title--lg { font-size: 26px; margin-bottom: 16px; }
  .cap__lead { margin: 0 0 16px; color: var(--vrt-text-muted); font-size: 16px; }
  .cap__ready { display: flex; flex-direction: column; gap: 14px; align-items: stretch; }
  .cap__big { font-size: 18px; min-height: 64px; gap: 10px; }
  .cap__photo { display: block; width: 100%; max-height: 360px; object-fit: cover; border-radius: var(--vrt-radius); }
  .cap__photo--small { max-height: 200px; }
  .cap__photo2-wrap {
    display: flex; flex-direction: column; gap: 8px;
    padding: 10px; border: 1px dashed var(--vrt-border); border-radius: var(--vrt-radius);
    background: var(--vrt-bg-alt);
  }
  .cap__photo2-label {
    font-size: 12px; font-weight: 700; text-transform: uppercase;
    color: var(--vrt-text-muted); letter-spacing: 0.5px;
  }
  .cap__photo2-actions { display: flex; gap: 8px; flex-wrap: wrap; }

  .cap__success-card { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px; text-align: center; }
  .cap__success-check {
    width: 88px; height: 88px; border-radius: 50%;
    background: var(--vrt-success); color: #fff;
    display: flex; align-items: center; justify-content: center;
  }
  .cap__success-card h2 { margin: 0; color: var(--vrt-navy); }
  .cap__success-card p { margin: 0; color: var(--vrt-text-muted); }

  .cap__gps { padding: 10px 12px; border-radius: var(--vrt-radius); font-size: 14px; }
  .cap__gps--ok { background: #e6f6f1; color: var(--vrt-success); border: 1px solid #b6e0d2; }
  .cap__gps--info { background: #eef3fa; color: var(--vrt-navy); border: 1px solid #cdd9ee; }
  .cap__gps--err { background: #fdecea; color: var(--vrt-danger); border: 1px solid #f5c6c2; display: flex; flex-direction: column; gap: 8px; }
  .cap__gps-city { font-size: 12px; margin-top: 4px; opacity: 0.9; }
  .cap__gps-geo { font-size: 12px; margin-top: 2px; opacity: 0.7; font-style: italic; }
  .cap__gps-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .cap__gps-btn { min-height: 40px; padding: 0 12px; font-size: 14px; }
  .cap__manual { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
  .cap__manual .vrt-input { flex: 1 1 140px; min-height: 44px; }

  /* ----- Wizard layout ----- */
  .wz__main { padding: 16px 16px 96px; }
  .wz__container { max-width: 600px; width: 100%; margin: 0 auto; display: flex; flex-direction: column; gap: 18px; }

  .wz__progress-wrap {
    position: sticky; top: 0; z-index: 50;
    background: var(--vrt-bg-alt);
    padding: 10px 16px 8px;
    border-bottom: 1px solid var(--vrt-border);
  }
  .wz__progress-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; max-width: 600px; margin: 0 auto; }
  .wz__progress-text { font-size: 14px; font-weight: 700; color: var(--vrt-navy); }
  .wz__progress-meta { display: flex; align-items: center; gap: 8px; min-width: 0; }
  .wz__mini-photo { width: 32px; height: 32px; object-fit: cover; border-radius: 6px; border: 1px solid var(--vrt-border); }
  .wz__city { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: var(--vrt-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px; }
  .wz__progress-bar { margin-top: 8px; height: 6px; background: var(--vrt-border); border-radius: 999px; overflow: hidden; max-width: 600px; margin-left: auto; margin-right: auto; }
  .wz__progress-fill { height: 100%; background: var(--vrt-teal); transition: width 220ms ease; }

  .wz__question { display: flex; flex-direction: column; gap: 14px; padding: 8px 0 16px; }
  .wz__question--priority { padding: 12px; background: #fff5f5; border: 2px solid var(--vrt-danger); border-radius: var(--vrt-radius); }
  .wz__hint { font-size: 13px; color: var(--vrt-text-muted); text-transform: uppercase; letter-spacing: 0.6px; font-weight: 600; }
  .wz__label { margin: 0 0 6px; font-size: 24px; line-height: 1.25; color: var(--vrt-navy); font-weight: 700; }
  .wz__counter { font-size: 14px; color: var(--vrt-text-muted); margin-top: 4px; }
  .wz__priority-banner {
    background: var(--vrt-danger); color: #fff;
    padding: 8px 12px; border-radius: 6px;
    font-size: 13px; font-weight: 700; letter-spacing: 0.5px;
    display: inline-flex; align-items: center; gap: 8px;
    align-self: flex-start;
  }
  .wz__priority-alert {
    background: var(--vrt-danger); color: #fff;
    padding: 12px 14px; border-radius: var(--vrt-radius);
    font-size: 14px; font-weight: 600; text-align: center;
  }

  /* Anomalie (violet) — calque sur Priorité Life */
  .wz__question--anomaly {
    padding: 12px; background: #f5f3ff; border: 2px solid #7c3aed; border-radius: var(--vrt-radius);
  }
  .wz__anomaly-banner {
    background: #7c3aed; color: #fff;
    padding: 8px 12px; border-radius: 6px;
    font-size: 13px; font-weight: 700; letter-spacing: 0.5px;
    display: inline-flex; align-items: center; gap: 8px;
    align-self: flex-start;
  }
  .wz__anomaly-alert {
    background: #7c3aed; color: #fff;
    padding: 12px 14px; border-radius: var(--vrt-radius);
    font-size: 14px; font-weight: 600; text-align: center;
  }
  .wz__big-tile--anomaly {
    border-color: #dc2626;
    color: #dc2626;
    background: #fff;
  }
  .wz__big-tile--anomaly-on {
    background: #dc2626 !important;
    border-color: #dc2626 !important;
    color: #fff !important;
  }

  .wz__tiles { display: flex; flex-direction: column; gap: 12px; }
  .wz__tile {
    position: relative;
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    width: 100%;
    min-height: 64px; padding: 18px 22px;
    border: 2px solid var(--vrt-border); border-radius: var(--vrt-radius);
    background: var(--vrt-bg-alt); color: var(--vrt-navy);
    font-size: 18px; font-weight: 500;
    cursor: pointer; user-select: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    text-align: left;
    box-sizing: border-box;
  }
  .wz__tile:hover:not(:disabled) { border-color: var(--vrt-teal); }
  .wz__tile--on {
    background: #2EB6A7;
    border-color: #2EB6A7;
    color: #fff;
    font-weight: 700;
  }
  .wz__tile--on .wz__tile-label { color: #fff; }
  .wz__tile:disabled { opacity: 0.6; cursor: not-allowed; }
  .wz__tile:focus-visible { outline: 3px solid var(--vrt-teal); outline-offset: 2px; }
  .wz__tile-label { flex: 1; }
  .wz__tile-check { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }

  .wz__input { font-size: 19px; min-height: 64px; padding: 14px 16px; }
  .wz__textarea { font-size: 17px; min-height: 200px; padding: 14px 16px; resize: vertical; }
  .wz__number { font-size: 28px; min-height: 80px; text-align: center; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; padding: 16px 18px; font-weight: 700; }

  .wz__yesno { display: flex; gap: 12px; }
  .wz__big-tile {
    flex: 1; min-height: 120px; padding: 18px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px;
    border: 2px solid var(--vrt-border); border-radius: var(--vrt-radius);
    background: var(--vrt-bg-alt); color: var(--vrt-navy);
    font-size: 24px; font-weight: 800;
    cursor: pointer; user-select: none;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  }
  .wz__big-tile:hover:not(:disabled) { border-color: var(--vrt-teal); }
  .wz__big-tile--on {
    background: #2EB6A7;
    border-color: #2EB6A7;
    color: #fff;
  }
  .wz__big-tile--no-on {
    background: var(--vrt-text-muted);
    border-color: var(--vrt-text-muted);
    color: #fff;
  }
  .wz__big-tile--priority {
    border-color: #c0392b;
    color: #c0392b;
    background: #fff;
  }
  .wz__big-tile--priority-on {
    background: #c0392b !important;
    border-color: #c0392b !important;
    color: #fff !important;
  }
  .wz__big-tile--single {
    width: 100%;
    min-height: 160px;
    font-size: 28px;
  }
  .wz__big-tile--single .wz__big-text { font-size: 26px; text-align: center; }
  .wz__big-text { font-size: 32px; line-height: 1; }
  .wz__big-check { display: block; }
  .wz__big-tile:disabled { opacity: 0.6; cursor: not-allowed; }

  .wz__required-error {
    background: #fdecea; color: var(--vrt-danger);
    border: 1px solid #f5c6c2; border-radius: var(--vrt-radius);
    padding: 12px 14px; font-size: 15px; font-weight: 600;
  }

  .wz__nav {
    position: sticky; bottom: 0; z-index: 50;
    background: var(--vrt-bg);
    border-top: 1px solid var(--vrt-border);
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    display: flex; gap: 12px;
  }
  .wz__nav-btn { flex: 1; min-height: 64px; font-size: 17px; gap: 6px; }
  .wz__nav-prev { flex: 1; }
  .wz__nav-next { flex: 2; font-weight: 700; }
  .wz__nav-next.is-disabled { opacity: 0.5; }

  /* ----- Summary screen ----- */
  .wz__summary-main { padding: 16px 16px 96px; }
  .wz__summary-error {
    background: #fdecea; color: var(--vrt-danger);
    border: 1px solid #f5c6c2; border-radius: var(--vrt-radius);
    padding: 12px 14px; font-size: 15px;
  }
  .wz__summary-error ul { margin: 8px 0 0; padding-left: 18px; }
  .wz__summary-link {
    background: none; border: none; padding: 0;
    color: var(--vrt-danger); font-weight: 700; text-decoration: underline; cursor: pointer;
    font-size: inherit;
  }

  .wz__sum-photo-wrap { position: relative; background: #000; border-radius: var(--vrt-radius); overflow: hidden; }
  .wz__sum-photo { display: block; width: 100%; max-height: 200px; object-fit: cover; }
  .wz__sum-retake {
    position: absolute; right: 8px; bottom: 8px;
    min-height: 40px; padding: 0 12px; font-size: 13px;
    background: rgba(255,255,255,0.92);
  }

  .wz__sum-section {
    background: #fff; border: 1px solid var(--vrt-border); border-radius: var(--vrt-radius);
    padding: 14px; display: flex; flex-direction: column; gap: 4px;
  }
  .wz__sum-section-title {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px;
    color: var(--vrt-text-muted); font-weight: 700;
    padding-bottom: 6px; margin-bottom: 4px;
    border-bottom: 1px solid var(--vrt-border);
  }
  .wz__sum-row {
    display: grid; grid-template-columns: 1fr 1fr auto; gap: 12px;
    padding: 10px 4px; border: none; background: none; text-align: left;
    align-items: center;
    border-bottom: 1px solid var(--vrt-border);
  }
  .wz__sum-row:last-child { border-bottom: none; }
  .wz__sum-row--clickable { cursor: pointer; border-radius: 6px; }
  .wz__sum-row--clickable:hover { background: var(--vrt-bg-alt); }
  .wz__sum-row--missing { background: rgba(192, 57, 43, 0.06); }
  .wz__sum-q { font-size: 14px; color: var(--vrt-text-muted); font-weight: 600; }
  .wz__sum-a { font-size: 15px; color: var(--vrt-navy); font-weight: 600; word-break: break-word; }
  .wz__sum-a--missing { color: var(--vrt-text-muted); font-style: italic; font-weight: 400; }
  .wz__sum-edit { color: var(--vrt-text-muted); flex-shrink: 0; }

  /* ----- Mobile tweaks ----- */
  @media (max-width: 480px) {
    .cap__main { padding: 12px; }
    .wz__main { padding: 12px 12px 96px; }
    .wz__progress-wrap { padding: 8px 12px; }
    .wz__label { font-size: 22px; }
    .wz__tile { font-size: 17px; padding: 16px 18px; min-height: 60px; }
    .wz__big-tile { min-height: 100px; }
    .wz__big-text { font-size: 28px; }
    .wz__sum-row { grid-template-columns: 1fr; gap: 4px; }
    .wz__sum-edit { display: none; }
  }

  /* ----- Desktop centering ----- */
  @media (min-width: 720px) {
    .wz__container { max-width: 600px; }
    .wz__nav { justify-content: center; }
    .wz__nav-btn { max-width: 280px; }
  }
`;
